import type { Summary } from './types';

export function renderWordCloud(summary: Summary): HTMLElement {
  const container = document.createElement("div");
  container.className = "word-cloud";

  const words = summary.words ?? [];
  const weights = summary.weights ?? [];

  if (!words.length || !weights.length) {
    container.innerHTML = "<div class='empty-state'>No words available.</div>";
    return container;
  }

  // Compute weighted average color if tokens look like hex colors
  function parseHexColor(input: string): { hex: string; r: number; g: number; b: number } | null {
    if (!input) return null;
    let t = String(input).trim();
    if (!t) return null;
    if (t.startsWith('#')) t = t.slice(1);
    if (t.length === 3) t = t.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(t)) return null;
    const n = parseInt(t, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return { hex: `#${t.toUpperCase()}`, r, g, b };
  }

  let avgHex: string | undefined;
  try {
    let sumW = 0, sumR = 0, sumG = 0, sumB = 0, valid = 0;
    words.forEach((w, i) => {
      const parsed = parseHexColor(w);
      const wtRaw = weights[i];
      const wt = typeof wtRaw === 'number' ? wtRaw : Number(wtRaw) || 0;
      if (parsed && wt > 0) {
        valid += 1; sumW += wt; sumR += parsed.r * wt; sumG += parsed.g * wt; sumB += parsed.b * wt;
      }
    });
    if (valid >= 1 && sumW > 0) {
      const r = Math.round(sumR / sumW);
      const g = Math.round(sumG / sumW);
      const b = Math.round(sumB / sumW);
      avgHex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0').toUpperCase()).join('')}`;
    }
  } catch {}

  const minFont = 16;
  const maxFont = 48;
  const counts = weights.map((w) => Math.max(0, Number(w) || 0));
  const maxCount = Math.max(...counts) || 1;
  const entries = words.map((word, i) => {
    const count = counts[i] ?? 0;
    return { text: word, count, size: minFont + (count / maxCount) * (maxFont - minFont) } as any;
  });

  const d3: any = (globalThis as any).d3 as any;
  // Detect if a word looks like a hex color and return a CSS color string
  function getWordColor(text: string): string | null {
    if (!text) return null;
    const t = String(text).trim();
    // Accept forms: #RRGGBB, #RGB, RRGGBB, RGB (hex only)
    const hex6 = /^#?[0-9A-Fa-f]{6}$/;
    const hex3 = /^#?[0-9A-Fa-f]{3}$/;
    if (hex6.test(t) || hex3.test(t)) {
      return t.startsWith('#') ? t : `#${t}`;
    }
    return null;
  }
  const cloud: any = (d3 as any).layout.cloud;
  const TOP_K = Math.min(5, Math.max(1, Math.round(entries.length * 0.08)));
  const sorted = [...entries].sort((a: any, b: any) => (b.count || 0) - (a.count || 0));
  const cutoff = sorted[Math.min(TOP_K - 1, sorted.length - 1)]?.count ?? 0;
  const isHighlight = (count: number) => count > 0 && count >= cutoff;

  const respondentTotal = typeof summary.n === 'number' && summary.n > 0
    ? summary.n
    : entries.reduce((acc: number, e: any) => acc + (e.count || 0), 0) || 1;

  function measure(): { width: number; height: number } {
    const rect = container.getBoundingClientRect();
    const cs = window.getComputedStyle(container);
    const w = Math.max(300, Math.round(rect.width || container.clientWidth || Number.parseFloat(cs.width) || 320));
    const h = Math.max(400, Math.round(rect.height || container.clientHeight || Number.parseFloat(cs.height) || 400));
    return { width: w, height: h };
  }

  let lastW = 0;
  let lastH = 0;
  let rafA = 0;
  let rafB = 0;
  const disposers: Array<() => void> = [];

  function renderNow(): void {
    const { width, height } = measure();
    if (width === lastW && height === lastH) return;
    lastW = width; lastH = height;

    container.innerHTML = '';
    if (avgHex) {
      const header = document.createElement('div');
      header.style.display = 'flex';
      header.style.alignItems = 'center';
      header.style.gap = '10px';
      header.style.margin = '0 0 6px 0';
      const sw = document.createElement('div');
      sw.style.width = '40px'; sw.style.height = '26px'; sw.style.borderRadius = '6px';
      sw.style.border = '1px solid rgba(148,163,184,0.28)';
      sw.style.background = avgHex;
      const label = document.createElement('div');
      label.textContent = `Average color: ${avgHex}`;
      label.style.fontWeight = '600'; label.style.color = '#475569'; label.style.fontSize = '0.95rem';
      header.appendChild(sw); header.appendChild(label);
      container.appendChild(header);
    }
    // Scale font sizes responsively based on container width
    const fontScale = Math.min(1.5, Math.max(0.6, width / 500));
    const scaledMaxFont = 48 * fontScale;
    const scaledMinFont = 16 * fontScale;
    const scaledEntries = entries.map((e: any) => ({
      ...e,
      size: scaledMinFont + ((e.count / maxCount) * (scaledMaxFont - scaledMinFont))
    }));
    const layout = cloud()
      .size([width, height])
      .words(scaledEntries)
      .padding(3)
      .rotate(() => 0)
      .font("Inter, sans-serif")
      .fontSize((d: any) => d.size)
      .on("end", (cloudWords: any[]) => {
        const svg = d3
          .select(container)
          .append("svg")
          .attr("width", width)
          .attr("height", height);

        const group = svg
          .append("g")
          .attr("transform", `translate(${width / 2},${height / 2})`);

        group
          .selectAll("text")
          .data(cloudWords)
          .enter()
          .append("text")
          .style("font-size", (d: any) => `${d.size}px`)
          .style("font-family", "Inter, sans-serif")
          .style("fill", (d: any) => {
            const c = getWordColor(d.text);
            if (c) return c;
            return isHighlight(d.count ?? 0) ? "#0249BD" : "#000000";
          })
          .attr("text-anchor", "middle")
          .attr("transform", (d: any) => `translate(${d.x},${d.y})rotate(${d.rotate})`)
          .text((d: any) => d.text)
          .append("title")
          .text((d: any) => {
            const count = Math.max(0, Number(d.count) || 0);
            const pct = respondentTotal ? (count / respondentTotal) * 100 : 0;
            return `${d.text}\nCount: ${count}\nShare: ${pct.toFixed(1)}%`;
          });

        // Auto-fit: scale and center the group to fill available space
        try {
          const bbox = (group.node() as SVGGraphicsElement).getBBox();
          const pad = 8;
          const scaleX = (width - pad * 2) / (bbox.width || 1);
          const scaleY = (height - pad * 2) / (bbox.height || 1);
          const scale = Math.min(scaleX, scaleY);
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y + bbox.height / 2;
          group.attr(
            "transform",
            `translate(${width / 2},${height / 2}) scale(${scale}) translate(${-cx},${-cy})`
          );
        } catch {}
      });
    layout.start();
  }

  function scheduleRender(): void {
    if (rafA) return;
    rafA = requestAnimationFrame(() => {
      rafA = 0;
      rafB = requestAnimationFrame(() => {
        rafB = 0;
        renderNow();
      });
    });
  }

  scheduleRender();
  if ((globalThis as any).ResizeObserver) {
    const ro = new (globalThis as any).ResizeObserver(() => scheduleRender());
    ro.observe(container);
    disposers.push(() => { try { ro.disconnect(); } catch {} });
  }

  // Ensure pending animation frames are cancelled on destroy
  disposers.push(() => { try { if (rafA) cancelAnimationFrame(rafA); } catch {}; try { if (rafB) cancelAnimationFrame(rafB); } catch {} });

  // Register destroy hook with global chart registry
  (globalThis as any).__activeCharts = (globalThis as any).__activeCharts || [];
  (globalThis as any).__activeCharts.push({ destroy: () => { try { disposers.forEach((fn) => fn()); } catch {} } });

  return container;
}
